import { Enumeration } from './enum.class';
import { ValidationBase } from './validation-base.class';
import { LogicFnRef, PrimitiveType, Storage } from './workflow.type';
import { dependencies } from './workflow.dependency';
import { StatefulService } from '@app/service/stateful.service';
import { SawDust } from '@cudirect/sawdust/sawdust';

export const PATH_ITERATION_SYMBOL = '$';
export const PATH_PAGE_ITERATION_SYMBOL = '#';
export const FIELD_ITERATION_SYMBOL = '*';

/**
 * Front-end for value state
 */
export class StatefulValue extends ValidationBase {
  /**
   * Storage unit specific config
   * TODO: make this readonly again once the config is properly loaded
   * and checkconfig is removed
   */
  public config: Storage;

  /**
   * Protocol of storageId (defines storage backend)
   */
  public readonly protocol = this.id.split(':')[0];

  /**
   * Indicates if this has been loaded at least once in the past
   */
  public isLoaded = false;

  /**
   * Backend reference
   */
  protected readonly backend = dependencies.storageService.backends[this.protocol];

  /**
   * onChange() subscriptions
   */
  private readonly onChangeCallbacks: (() => void)[] = [];

  /**
   * updateCallback() subscriptions
   */
  private readonly afterPatchCallbacks: (() => void)[] = [];

  /**
   * Current value (if different than `.saved`)
   */
  private current: PrimitiveType;

  /**
   * Bump this number up for locking (i.e on focusing a field sourcing this value) or down to unlock (on blur)
   */
  private _saveLock = false;

  /**
   * Saved (aka original) value
   */
  private saved: PrimitiveType;

  public get logger(): SawDust {
    return this.statefulService.logger;
  }

  /**
   * Unique stateful value
   * @param id static ID including the storage backend (ex: 'api:path/x/y')
   * @param iterationId optional dynamic part for the id (replacement for the `$` sign)
   * @param pageIterationId optional page loop id (replacement for the `#` sign)
   */
  constructor(
    private readonly statefulService: StatefulService,
    public readonly id: string,
    public iterationId?: number,
    public pageIterationId?: number
  ) {
    super();
    // const protocol = id.split(':')[0];

    // const backend = dependencies.storageService.backends[protocol];

    const info = dependencies.storageInfoService.parse(id);

    if (iterationId != null && this.backend.type === 'api') {
      const resolvedPageIteration = info?.pageIterationId ?? this.pageIterationId;
      const arrayPath = info?.storageId ?? id;

      let currentSize = this.statefulService.arraySize(arrayPath, resolvedPageIteration);

      if (currentSize <= iterationId) {
        const pathForAdd =
          resolvedPageIteration != null && arrayPath.indexOf(PATH_PAGE_ITERATION_SYMBOL) > -1
            ? arrayPath.replace(PATH_PAGE_ITERATION_SYMBOL, String(resolvedPageIteration))
            : arrayPath;

        this.logger.warn('Expanding array for missing iteration', {
          storageId: pathForAdd,
          iterationId,
          pageIterationId: resolvedPageIteration
        });

        while (currentSize <= iterationId) {
          this.statefulService.arrayAdd(pathForAdd);
          const newSize = this.statefulService.arraySize(arrayPath, resolvedPageIteration);
          if (newSize === currentSize) {
            break;
          }
          currentSize = newSize;
        }
      }

      const finalSize = this.statefulService.arraySize(arrayPath, resolvedPageIteration);
      if (finalSize <= iterationId) {
        throw new Error(`Can not implicitly create array instance: ${id}@${iterationId}, array size: ${finalSize}`);
      }
    }
    this.config = info && info.config;
    if (!this.config) {
      if (this.backend.type === 'api') {
        throw new Error(`Could not find storage info config for ${id}`);
      }
      this.config = { id, type: 'string', validation: {} };
    } else {
      this.statefulService.ensureConfig(this.config, id);
    }
  }

  /**
   * Temporary fix to resolve this.config being null
   * https://dev.azure.com/cudirect/Origence/_workitems/edit/263108
   */
  public checkConfig(config) {
    let attempts = 0;
    const maxAttempts = 1000;
    while (!config) {
      const info = dependencies.storageInfoService.parse(this.id);
      if (attempts >= maxAttempts) {
        this.logger.info('Failed to resolve config after 1000 attempts on ID:', { id: this.id });
        this.config = { id: this.id, type: 'string', validation: {} };
        break;
      }
      // Attempt to populate this.config
      config = info?.config;
      attempts++;
    }
    this.config = config;
  }

  /**
   * Value is modified, but not saved yet
   */
  public get isChanged(): boolean {
    return typeof this.current !== 'undefined';
  }

  /**
   * Backend has started an async operation regarding this field, and is not finished yet
   */
  public get isLoading(): boolean {
    return this.backend.isLoading;
  }

  /**
   * Saved (aka original) value
   */
  public get savedValue(): PrimitiveType {
    return this.saved;
  }

  /**
   * convert config.validation object to LogicFnRef array
   */
  public get validationLogicFn(): LogicFnRef[] {
    this.checkConfig(this.config);
    if (!this.config?.validation) {
      return [];
    }

    return Object.keys(this.config.validation).map((validationFn) => {
      const arg: PrimitiveType[] = [];
      if (Array.isArray(this.config.validation[validationFn])) {
        this.config.validation[validationFn].forEach((a: PrimitiveType) => arg.push(dependencies.stringVariableService.parse(a)));
      } else {
        arg.push(dependencies.stringVariableService.parse(this.config.validation[validationFn]));
      }
      return <LogicFnRef>[validationFn, ...arg];
    });
  }

  /**
   * Current value
   */
  public get value(): PrimitiveType {
    this.checkConfig(this.config);

    if (!this.config) {
      this.logger.info(this.id + ' has no type', { id: this.id });
    }

    const value = this.isChanged ? this.current : this.saved;
    const type = this.config?.type;
    if (value == null) {
      return value;
    }
    if (type === 'boolean') {
      return !!value;
    }
    if (type === 'number') {
      return this.statefulService.numericValue(value);
    }
    return String(value);
  }

  /**
   * Current value as number (or null or undefined)
   */
  public get valueNumber(): number {
    return this.statefulService.numericValue(this.value);
  }

  /**
   * Current value as number (null or undefined will be 0)
   */
  public get valueNumberOnly(): number {
    const value = this.valueNumber;
    return value == null ? 0 : value;
  }

  /**
   * Current value as string (or null or undefined)
   */
  public get valueString(): string {
    const value = this.value;
    return value == null ? <null>value : String(value);
  }

  /**
   * Current value as string (null and undefined become empty string)
   */
  public get valueStringOnly(): string {
    const value = this.value;
    return value == null ? '' : String(value);
  }

  /**
   * Impose new current value over saved value (or revert if they are identical)
   */
  // tslint:disable-next-line: adjacent-overload-signatures
  // eslint-disable-next-line @typescript-eslint/adjacent-overload-signatures
  public set value(value: PrimitiveType) {
    this.checkConfig(this.config);

    if (!this.config) {
      this.logger.log(this.id + ' has no type', { id: this.id });
    }
    const sanitizedValue =
      value == null
        ? value
        : this.config?.type === 'boolean'
        ? !!value
        : this.config?.type === 'number'
        ? this.statefulService.numericValue(value)
        : String(value);
    if (sanitizedValue !== this.current) {
      const changed = this.value !== sanitizedValue;
      if (sanitizedValue === this.saved) {
        this.revert();
      } else {
        this.current = sanitizedValue;
      }

      if (changed) {
        for (const callback of this.onChangeCallbacks) {
          callback();
        }
      }

      this.save();
    }
  }

  /**
   * @inheritdoc
   */
  public hasValidation(fnName: string): PrimitiveType[] {
    return dependencies.logicService.hasFunction(fnName, this.validationLogicFn);
  }

  /**
   * Trigger loading from backend + mark loaded value as saved/original
   */
  public load(callback?: () => void): void {
    dependencies.storageService.load(this.id, (err, value) => {
      this.isLoaded = true;
      if (!err) {
        this.setSavedValue(value);
      }
      if (callback) {
        callback();
      }
      // TODO: add error propagation
    });
  }

  /**
   * Callbacks for value change
   * @param callback fn to call on value change
   */
  public onChange(callback: () => void): void {
    this.onChangeCallbacks.push(callback);
  }

  /**
   * Callbacks: after value patched
   * @param callback fn to call after value updated
   */
  public afterPatch(callback: () => void): void {
    this.afterPatchCallbacks.push(callback);
  }

  /**
   * Trigger revalidation
   */
  public revalidate(): string[] {
    return this.spreadValidation(
      dependencies.logicService.evaluate(this.validationLogicFn, { value: this }, 'collect', this.iterationId, this.pageIterationId)
    );
  }

  /**
   * Roll back change in value
   */
  public revert(): void {
    delete this.current;
  }

  public isSaving;
  /**
   * Trigger saving to backend + merge current value to original if successful
   */
  public save(): Promise<boolean> {
    if (
      !this.valid ||
      (this.value != null && this.config.enum && !new Enumeration(this.config.enum, true).values.find((i) => i.value === this.value))
    ) {
      return Promise.resolve(false);
    }
    if (this.isChanged && !this._saveLock) {
      this.isSaving = true;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      return new Promise<boolean>((resolve, reject) => {
        dependencies.storageService.save(this.id, this.value, (err) => {
          if (!err) {
            this.saved = this.value;
            dependencies.stateManagementService.appendValue(this.id, this.saved);
            delete this.current;
            resolve(true);
          }
          resolve(false);
          this.isSaving = false;
          // TODO: add error propagation
        });
      });
    }
  }

  /**
   * Bump up _saveLock counter
   */
  public saveLock(): void {
    this._saveLock = true;
  }

  /**
   * Knock down _saveLock counter. Check if saving is due
   * @param noSave prevents saving on reaching 0-lock
   */
  public saveUnlock(noSave?: boolean): void {
    this._saveLock = false;
    if (!this._saveLock && !noSave) {
      this.save();
    }
  }

  /**
   * Unsubscribe on change callbacks
   * @param callback function ref for onChange callback
   */
  public unsubscribe(callback: () => void): void {
    let pos = this.onChangeCallbacks.indexOf(callback);
    if (pos > -1) {
      this.onChangeCallbacks.splice(pos, 1);
    }

    pos = this.afterPatchCallbacks.indexOf(callback);
    if (pos > -1) {
      this.afterPatchCallbacks.splice(pos, 1);
    }
  }

  /**
   * Set the saved (and current) value, propagates change (if any)
   * @param value new value to be treated as saved
   */
  public setSavedValue(value: PrimitiveType): void {
    this.saved = value;
    if (this.current === value) {
      delete this.current;
    }
    for (const callback of this.afterPatchCallbacks) {
      callback();
    }
  }
}
